// src/redux/scanSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { tenantService } from "../api/api"; // Adjust path to your api service
import { LeakGuardService } from "@/src/services/leakGuard/LeakGuardService";

// --- Interfaces ---
export interface SastResult {
  id: string;
  fileName: string;
  lineNumber: number;
  vulnerabilityType: string;
  severity: "Low" | "Medium" | "High" | "Critical";
  suggestedFix?: string;
  description: string;
  cweId?: string;
  confidence: number;
}

export interface ScaResult {
  id: string;
  packageName: string;
  version: string;
  cveId: string;
  severity: "Low" | "Medium" | "High" | "Critical";
  fixSuggestion: string;
  description: string;
  publishedDate: string;
  cvssScore?: number;
  report_url?: string;
}

interface ScanState {
  isScanning: boolean;
  scanProgress: number;
  runningScanType: 'SAST' | 'SCA' | null; 
  activeTab: string;
  sastResults: SastResult[];
  scaResults: ScaResult[];
  sastPdfUrl: string | null;
  scaPdfUrl: string | null;
  error: string | null;
  scanType: "SAST" | "SCA" | null;
  lastSastScanSuccess: boolean | null; // null = never run
  lastScaScanSuccess: boolean | null;
  // [NEW] Controls which sub-tab is visible (File, Zip, or Github)
  sastInputType: "file" | "zip" | "github"; 
  
  // [NEW] Tracks which specific type is CURRENTLY scanning
  // This prevents the loader from showing up in the wrong tab
  runningSastType: "file" | "zip" | "github" | null;
  
  // [NEW] Stores the name of the file or repo currently being scanned
  currentScanTarget: string | null;

  sastFile: File | null;
  zipFile: File | null;
  scaFile: File | null;
  githubRepo: string;
  githubBranch: string;
}

const initialState: ScanState = {
  isScanning: false,
  scanProgress: 0,
  activeTab: "sast",
  runningScanType: null,
  sastResults: [],
  scaResults: [],
  sastPdfUrl: null,
  scaPdfUrl: null,
  error: null,
  scanType: null,
  lastSastScanSuccess: null,
  lastScaScanSuccess: null,

  // Defaults
  sastInputType: "file",
  runningSastType: null,
  currentScanTarget: null,

  sastFile: null,
  zipFile: null,
  scaFile: null,
  githubRepo: "",
  githubBranch: "main",
};

// --- Async Thunks ---

export const runSastScan = createAsyncThunk(
  "scan/runSastScan",
  async (
    payload: { 
      type: "file" | "zip" | "github"; 
      file?: File; 
      repo?: string; 
      branch?: string; 
      token?: string; 
      scanners?: string[] 
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const controller = new AbortController(); 
      
      // Start fake progress for UI feedback
      const progressInterval = setInterval(() => {
        dispatch(incrementProgress(Math.random() * 2.5 + 0.5));
      }, 1500);

      let apiResponse;
      if (payload.type === "file" && payload.file) {
        const language = "javascript"; // You might want to pass this or detect it
        apiResponse = await tenantService.scanFile(payload.file, language, controller.signal, payload.scanners);
      } else if (payload.type === "zip" && payload.file) {
        apiResponse = await tenantService.scanSASTFolder(payload.file, controller.signal, payload.scanners);
      } else if (payload.type === "github" && payload.repo) {
        apiResponse = await tenantService.scanGitRepo(
          payload.repo, 
          payload.branch || "main", 
          controller.signal, 
          payload.token, 
          payload.scanners
        );
      }
      

      // Check if the API response indicates failure
      if (apiResponse && apiResponse.success === false) {
        const errorMessage =
          typeof apiResponse.message === "string"
            ? apiResponse.message
            : apiResponse.message?.message || "Scan failed";

        return rejectWithValue(errorMessage);
      }

      clearInterval(progressInterval);
      dispatch(setScanProgress(100));

      // Extract pdf_url first regardless of results
      const pdfUrl = apiResponse?.pdf_url || null;

      // Process Results
      if (apiResponse?.Results && Array.isArray(apiResponse.Results)) {
         let vulnerabilityId = 1;
         const processedResults = apiResponse.Results.flatMap((fileResult: any) => {
            if (fileResult.vulnerabilities && Array.isArray(fileResult.vulnerabilities)) {
              return fileResult.vulnerabilities.map((vuln: any) => {
                const severity = (vuln.Severity as "Low" | "Medium" | "High" | "Critical") || "Medium";
                return {
                  id: (vulnerabilityId++).toString(),
                  fileName: fileResult.file_path || "Unknown",
                  lineNumber: parseInt(vuln.File?.match(/:(\d+)$/)?.[1] || "0", 10),
                  vulnerabilityType: vuln.Issue || "Security Vulnerability",
                  severity: severity,
                  description: vuln.Description || "Detected issue",
                  cweId: vuln.CWE,
                  confidence: 85,
                } as SastResult;
              });
            }
            return [];
         });
         return { results: processedResults, pdfUrl };
      }
      return { results: [], pdfUrl };

    } catch (error: any) {
       console.log("SAST Scan Error:", error);
      return rejectWithValue(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : error?.response?.data?.message?.message || "Scan failed"
      );
    }
  }
);

export const runScaScan = createAsyncThunk(
  "scan/runScaScan",
  async (payload: { file: File; scanners?: string[] }, { dispatch, rejectWithValue }) => {
    try {
      const controller = new AbortController();
      const progressInterval = setInterval(() => {
        dispatch(incrementProgress(Math.random() * 8 + 3));
      }, 400);

      const apiResponse = await tenantService.scanSCAFile(payload.file, controller.signal, payload.scanners);
      
      // Check if the API response indicates failure
      if (apiResponse && apiResponse.success === false) {
        clearInterval(progressInterval);
        const msg = apiResponse.message;
        return rejectWithValue(
          typeof msg === "object" && msg !== null ? msg.message : msg || "Scan failed"
        );
      }
      
      clearInterval(progressInterval);
      dispatch(setScanProgress(100));

      // Extract pdfUrl first regardless of results
      const pdfUrl = apiResponse?.pdf_url || apiResponse?.report_url || null;

      // Process Results
      let processedResults: ScaResult[] = [];
      if (apiResponse?.Results) {
        let vulnerabilityId = 1;
        processedResults = apiResponse.Results.flatMap((result: any) =>
          (result.vulnerabilities || []).map((vuln: any) => ({
            id: (vulnerabilityId++).toString(),
            packageName: vuln.Issue || "Unknown",
            version: result.language || "Unknown",
            cveId: vuln.Issue || "N/A",
            severity: vuln.Severity
              ? vuln.Severity.charAt(0).toUpperCase() + vuln.Severity.slice(1)
              : "Medium",
            fixSuggestion: "Update package",
            description: vuln.Description || "Issue",
            publishedDate: new Date().toISOString(),
          }))
        );
      }
      return { results: processedResults, pdfUrl };
    } catch (error: any) {
      const msg = error?.message;
      return rejectWithValue(
        typeof msg === "object" && msg !== null ? msg.message : msg || "Scan failed"
      );
    }
  }
);

const scanSlice = createSlice({
  name: "scan",
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    // [NEW] Action to manually switch the SAST sub-tab (File/Zip/Github)
    setSastInputType: (state, action: PayloadAction<"file" | "zip" | "github">) => {
      state.sastInputType = action.payload;
    },
    setScanProgress: (state, action: PayloadAction<number>) => {
      state.scanProgress = action.payload;
    },
    incrementProgress: (state, action: PayloadAction<number>) => {
      if (state.scanProgress < 90) state.scanProgress = Math.min(state.scanProgress + action.payload, 90);
    },
    setSastFile: (state, action: PayloadAction<File | null>) => {
      state.sastFile = action.payload;
    },
    setZipFile: (state, action: PayloadAction<File | null>) => {
      state.zipFile = action.payload;
    },
    setScaFile: (state, action: PayloadAction<File | null>) => {
      state.scaFile = action.payload;
    },
    setGithubRepo: (state, action: PayloadAction<string>) => {
      state.githubRepo = action.payload;
    },
    setGithubBranch: (state, action: PayloadAction<string>) => {
      state.githubBranch = action.payload;
    },
   resetScanState: (state) => {
      // Stop loading indicators
      state.isScanning = false;
      state.scanProgress = 0;
      state.error = null;
      state.runningScanType = null;
      state.sastResults = [];
      state.scaResults = [];
      state.sastInputType = "file";
      state.lastSastScanSuccess = null;
      state.lastScaScanSuccess = null;
      // Clear Files
      state.sastFile = null;
      state.zipFile = null; 
      state.scaFile = null;
      
      // Clear URLs
      state.sastPdfUrl = null;
      state.scaPdfUrl = null;
      
      // Clear Github info
      state.githubRepo = "";
      state.githubBranch = "main";
    },
    resetSastPdfUrl: (state) => {
      state.sastPdfUrl = null;
      state.sastResults = [];
      state.lastSastScanSuccess = null;
    },
    resetScaPdfUrl: (state) => {
      state.scaPdfUrl = null;
      state.scaResults = [];
      state.lastScaScanSuccess = null;
    },
  },
  extraReducers: (builder) => {
    // --- SAST ---
    builder.addCase(runSastScan.pending, (state, action) => {
      state.isScanning = true;
      state.error = null;
      state.scanType = "SAST";
      state.sastResults = [];
      state.sastPdfUrl = null;
      state.runningScanType = 'SAST';
      state.lastSastScanSuccess = null;
      
      // 1. Force the UI to switch to the tab where scan started
      state.sastInputType = action.meta.arg.type;
      
      // 2. Lock the 'Running' type so the loader only appears in this specific tab
      state.runningSastType = action.meta.arg.type;
      
      // 3. Store file/repo name so UI can display it during scan
      if (action.meta.arg.type === "file") {
        state.currentScanTarget = action.meta.arg.file?.name || "Unknown File";
      } else if (action.meta.arg.type === "zip") {
        state.currentScanTarget = action.meta.arg.file?.name || "Unknown Zip";
      } else {
        state.currentScanTarget = action.meta.arg.repo || "Unknown Repo";
      }
    });
    builder.addCase(runSastScan.fulfilled, (state, action) => {
      state.isScanning = false;
      state.runningSastType = null; // Unlock the loader
      state.sastResults = action.payload.results;
      state.sastPdfUrl = action.payload.pdfUrl;
      state.runningScanType = null;
      state.lastSastScanSuccess = true;
    });
    builder.addCase(runSastScan.rejected, (state, action) => {
      state.isScanning = false;
      state.runningSastType = null; // Unlock the loader
      state.error = action.payload as string || action.error.message || "An unknown error occurred";
      state.sastPdfUrl = null;
      state.runningScanType = null;
      state.lastSastScanSuccess = false;
    });

    // --- SCA ---
    builder.addCase(runScaScan.pending, (state) => {
      state.isScanning = true;
      state.error = null;
      state.scanType = "SCA";
      state.runningScanType = 'SCA';
      state.scaResults = [];
      state.scaPdfUrl = null;
      state.lastScaScanSuccess = null;
    });
    builder.addCase(runScaScan.fulfilled, (state, action) => {
      state.isScanning = false;
      state.scaResults = action.payload.results;
      state.scaPdfUrl = action.payload.pdfUrl;
      state.runningScanType = null;
      state.lastScaScanSuccess = true;
    });
    builder.addCase(runScaScan.rejected, (state, action) => {
      state.isScanning = false;
      state.runningScanType = null;
      state.error = action.payload as string || action.error.message || "SCA scan failed";
      state.scaPdfUrl = null;
      state.lastScaScanSuccess = false;
    });
  },
});

export const { 
setActiveTab, 
  setScanProgress, 
  incrementProgress, 
  resetScanState,
  setSastInputType,
  setSastFile,
  setZipFile,
  setScaFile,
  setGithubRepo,
  setGithubBranch,
  lastSastScanSuccess,
  lastScaScanSuccess,
  resetSastPdfUrl,
  resetScaPdfUrl,
} = scanSlice.actions;

export default scanSlice.reducer;
